import { call, put, takeEvery, takeLatest } from 'redux-saga/effects'
import {
  AdminFetchDocumentReviewSteps,
  AdminFetchDocumentReviewStep,
  AdminFetchReviewRejectionReasons,
  AdminApproveReviewStep,
  AdminRecollectReviewStep,
} from 'src/constants/actionTypes'
import { Endpoints } from 'src/constants/endpoints'
import type {
  ApproveReviewStepPayload,
  DocumentReviewQueueRow,
  DocumentReviewStepsResponse,
  DocumentReviewStep,
  RecollectReviewStepPayload,
  ReviewRejectionReason,
} from 'src/interfaces/documentReview'
import { sortReviewQueueByWaiting } from 'src/utils/documentReviewList'
import { normalizeApiListPayload } from 'src/utils/normalizeApiListPayload'
import { resolveErrorMessage } from 'src/utils/sagaErrorMessage'
import { captureSentryError } from 'src/utils/sentry'
import type { AsyncAction } from 'src/utils/reduxUtils'
import { callSecureApi } from './api/callApi'

export function* onFetchDocumentReviewSteps(action: AsyncAction<undefined>) {
  try {
    const { payload }: { payload: Partial<DocumentReviewStepsResponse> } = yield call(
      callSecureApi,
      Endpoints.InternalDocumentReviewSteps,
      { method: 'GET' },
    )

    // The endpoint answers `{ steps, total }`. `total` is `steps.length` server-side, so it is
    // not stored — a second source of truth for the same number can only go stale.
    const steps: DocumentReviewQueueRow[] = sortReviewQueueByWaiting(payload?.steps ?? [])

    yield put(AdminFetchDocumentReviewSteps.success(steps))

    if (action.next) {
      action.next(null, steps)
    }
  } catch (error: unknown) {
    captureSentryError(error, {
      message: '[saga.documentReview.onFetchDocumentReviewSteps] error',
    })

    yield put(AdminFetchDocumentReviewSteps.failed(error))

    if (action.next) {
      action.next(resolveErrorMessage(error, 'Failed to load the review queue'))
    }
  }
}

interface FetchDocumentReviewStepPayload {
  stepId: string
}

export function* onFetchDocumentReviewStep(action: AsyncAction<FetchDocumentReviewStepPayload>) {
  const stepId = action.payload?.stepId

  if (!stepId) {
    yield put(AdminFetchDocumentReviewStep.failed(new Error('Missing stepId')))

    if (action.next) {
      action.next('Missing stepId')
    }

    return
  }

  try {
    const { payload }: { payload: DocumentReviewStep } = yield call(
      callSecureApi,
      Endpoints.InternalDocumentReviewStep,
      {
        method: 'GET',
        params: [{ field: ':collectDataStepId', value: stepId }],
      },
    )

    yield put(AdminFetchDocumentReviewStep.success(payload))

    if (action.next) {
      action.next(null, payload)
    }
  } catch (error: unknown) {
    captureSentryError(error, {
      message: '[saga.documentReview.onFetchDocumentReviewStep] error',
    })

    yield put(AdminFetchDocumentReviewStep.failed(error))

    if (action.next) {
      action.next(resolveErrorMessage(error, 'Failed to load the document'))
    }
  }
}

export function* onFetchReviewRejectionReasons(action: AsyncAction<undefined>) {
  try {
    const { payload }: { payload: unknown } = yield call(
      callSecureApi,
      Endpoints.InternalDocumentReviewRejectionReasons,
      { method: 'GET' },
    )

    const reasons = normalizeApiListPayload<ReviewRejectionReason>(payload)

    // Sorted on a copy — `.sort()` mutates, and the payload is about to become store state.
    const sortedReasons = [...reasons].sort((first, second) => {
      return first.name.localeCompare(second.name)
    })

    yield put(AdminFetchReviewRejectionReasons.success(sortedReasons))

    if (action.next) {
      action.next(null, sortedReasons)
    }
  } catch (error: unknown) {
    captureSentryError(error, {
      message: '[saga.documentReview.onFetchReviewRejectionReasons] error',
    })

    yield put(AdminFetchReviewRejectionReasons.failed(error))

    if (action.next) {
      action.next(resolveErrorMessage(error, 'Failed to load rejection reasons'))
    }
  }
}

export function* onApproveReviewStep(action: AsyncAction<ApproveReviewStepPayload>) {
  try {
    yield call(callSecureApi, Endpoints.InternalDocumentReviewApproveStep, {
      method: 'POST',
      body: action.payload,
    })

    yield put(AdminApproveReviewStep.success())

    if (action.next) {
      action.next(null)
    }
  } catch (error: unknown) {
    captureSentryError(error, { message: '[saga.documentReview.onApproveReviewStep] error' })

    yield put(AdminApproveReviewStep.failed(error))

    if (action.next) {
      action.next(resolveErrorMessage(error, 'Failed to approve the document'))
    }
  }
}

export function* onRecollectReviewStep(action: AsyncAction<RecollectReviewStepPayload>) {
  try {
    yield call(callSecureApi, Endpoints.InternalDocumentReviewRecollectStep, {
      method: 'POST',
      body: action.payload,
    })

    yield put(AdminRecollectReviewStep.success())

    if (action.next) {
      action.next(null)
    }
  } catch (error: unknown) {
    captureSentryError(error, { message: '[saga.documentReview.onRecollectReviewStep] error' })

    yield put(AdminRecollectReviewStep.failed(error))

    if (action.next) {
      action.next(resolveErrorMessage(error, 'Failed to send the document back'))
    }
  }
}

export function* documentReviewSaga() {
  // Refresh, tab switch and the approve-then-advance loop all re-trigger this; only the newest
  // run matters.
  yield takeLatest(AdminFetchDocumentReviewSteps.type.REQUEST, onFetchDocumentReviewSteps)
  yield takeLatest(AdminFetchDocumentReviewStep.type.REQUEST, onFetchDocumentReviewStep)
  yield takeLatest(AdminFetchReviewRejectionReasons.type.REQUEST, onFetchReviewRejectionReasons)
  // Each approve or recollect must run — takeLatest would drop one if two fire in quick
  // succession (e.g. a doubled click before the button disables).
  yield takeEvery(AdminApproveReviewStep.type.REQUEST, onApproveReviewStep)
  yield takeEvery(AdminRecollectReviewStep.type.REQUEST, onRecollectReviewStep)
}
